import { type CalendarEntryDto, type MeDto, READINESS, type Readiness, type PlanningBoardDto, type PlanningQueueDto, shiftDate, type TaskDto, type TaskSegmentDto, readableTextColor, taskDays, todayInWarsaw, datePl } from '@bqcrm/shared';
import { ActionIcon, Badge, Box, Button, Card, Chip, Collapse, Group, Loader, ScrollArea, SegmentedControl, Stack, Text, TextInput, Tooltip, UnstyledButton, useMantineTheme } from '@mantine/core';
import { useElementSize, useMediaQuery } from '@mantine/hooks';
import { IconAlertTriangle, IconCarOff, IconChevronDown, IconChevronLeft, IconChevronRight, IconLock, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type DragEvent, Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { CompanyBadgeById, ReadinessMark, TradeBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { useTrades, useTaskTypeLabel } from '../../data';
import { dayRange, plural } from '../../format';
import { notifyError } from '../../notify';
import { ConflictMark, dayLabel, FleetMark, termText } from './common';
import { AssignDialog } from './AssignDialog';
import { RealizationDialog } from './RealizationDialog';
import { PlanDialog, type PlanDefaults } from './PlanDialog';
import { TaskPanel } from './TaskPanel';
import { TradeFilter } from './TradeFilter';

const LABEL_W = 190;
/** minimalna szerokość dnia: tydzień i 2 tygodnie mieszczą się bez przewijania, 4 tygodnie przewijają się w poziomie */
const MIN_DAY_W = 44;
const MIN_DAY_W_LONG = 56;
/** wysokość jednego toru (paska) w wierszu ekipy — dwie linie tekstu; pasek 44 px jak wpis w Kalendarzu */
const LANE_H = 50;
/** kolumna nazw przyklejona przy przewijaniu w poziomie */
const STICKY = { position: 'sticky', left: 0, zIndex: 3, background: 'var(--mantine-color-body)' } as const;
/** kolory typów zadań bez zakresu (przegląd, serwis ze zgłoszenia) */
const TYPE_COLOR: Record<string, string> = { przeglad: '#0ca678', serwis: '#7048e8', wizja: '#495057', pomiary: '#495057', inne: '#495057' };

/** skróty dni tygodnia w nagłówkach listy dziennej (telefon) */
const WD_SHORT = ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'];
const wdShort = (d: string) => WD_SHORT[new Date(`${d}T12:00:00Z`).getUTCDay()]!;

/** Czy wpis obejmuje dany dzień pracy (bez weekendów, gdy pomijane; z dniami dodanymi, bez wyłączonych). */
const segOnDay = (seg: TaskSegmentDto, d: string) =>
  !seg.excluded.includes(d) && (seg.added.includes(d) || (seg.dateFrom <= d && d <= seg.dateTo && !(seg.skipWeekends && dayLabel(d).weekend)));

const surname = (n: string | null) => (n ? n.split(' ').filter((x) => !/^(sp\.|z|o\.o\.|j\.|s\.c\.)$/i.test(x)).slice(-1)[0] ?? n : '');

type Drag = { type: 'task'; id: string } | { type: 'segment'; id: string; segmentId: string } | { type: 'scope'; scopeIds: string[]; caseId: string };
/** pasek = jeden wpis planu (dni pracy ekipy przy zadaniu) */
type Bar = { task: TaskDto; seg: TaskSegmentDto; lane: number; personal: boolean };

/** Wpisy zadania; dla zadań bez wpisów (dane sprzed wpisów) — jeden z terminu zadania. */
const segmentsOf = (t: TaskDto): TaskSegmentDto[] =>
  t.segments.length
    ? t.segments
    : t.dateFrom && t.dateTo
      ? [{ id: t.id, dateFrom: t.dateFrom, dateTo: t.dateTo, timeFrom: t.timeFrom, timeTo: t.timeTo, skipWeekends: t.skipWeekends, teamId: t.teamId, added: t.added, excluded: t.excluded }]
      : [];

/** Pasy zadań w wierszu: przydział do „torów”, żeby nachodzące zadania nie zasłaniały się. */
function lanes(items: Omit<Bar, 'lane'>[]): Bar[] {
  const out: Bar[] = [];
  const ends: string[] = [];
  for (const it of [...items].sort((a, b) => a.seg.dateFrom.localeCompare(b.seg.dateFrom) || (a.seg.timeFrom ?? '').localeCompare(b.seg.timeFrom ?? ''))) {
    let lane = ends.findIndex((e) => e < it.seg.dateFrom);
    if (lane < 0) lane = ends.length;
    ends[lane] = it.seg.dateTo;
    out.push({ ...it, lane });
  }
  return out;
}

type QueueFilter = 'wszystko' | 'kontynuacja' | 'serwis' | 'montaze' | 'inne';

/**
 * Planowanie (koordynator): po lewej kolejka „Do zaplanowania” (kliknij albo przeciągnij), po prawej siatka ekip × dni.
 * Zadania przydzielone osobom (bez ekipy) pokazują się w wierszu ekipy, do której te osoby należą.
 */
export function PlanningPage({ me }: { me: MeDto }) {
  const typeLabel = useTaskTypeLabel();
  const today = todayInWarsaw();
  // widok zaczyna się od dziś — liczy się to, co przed nami (montaże, terminarz)
  const [start, setStart] = useState(today);
  // link z powiadomienia (`?dzien=RRRR-MM-DD`, np. nieobecność koliduje z pracami) — plan od tego dnia
  const [linkParams, setLinkParams] = useSearchParams();
  useEffect(() => {
    const day = linkParams.get('dzien');
    if (!day) return;
    if (/^\d{4}-\d{2}-\d{2}$/.test(day)) setStart(day);
    setLinkParams({}, { replace: true });
  }, [linkParams, setLinkParams]);
  // domyślnie tydzień (najczytelniejszy); wybór zapamiętany w przeglądarce
  const [span, setSpanState] = useState(() => {
    try {
      return localStorage.getItem('plan-zakres') ?? '7';
    } catch {
      return '7';
    }
  });
  const setSpan = (v: string) => {
    setSpanState(v);
    try {
      localStorage.setItem('plan-zakres', v);
    } catch {
      /* bez pamięci wyboru */
    }
  };
  const to = shiftDate(start, Number(span) - 1);
  // filtr branż (kilka naraz): kolejka, ekipy w siatce i zadania; zapamiętany w przeglądarce
  const [tradeFilter, setTradeFilterState] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('plan-branze') ?? '[]') as string[];
    } catch {
      return [];
    }
  });
  const setTradeFilter = (v: string[]) => {
    setTradeFilterState(v);
    try {
      localStorage.setItem('plan-branze', JSON.stringify(v));
    } catch {
      /* bez pamięci wyboru */
    }
  };
  /** ekipa robi którąś z wybranych branż (bez filtra — każda) */
  const teamInFilter = (t: { tradeCodes: string[] }) => !tradeFilter.length || t.tradeCodes.some((c) => tradeFilter.includes(c));
  /** osoba spoza ekip robi którąś z wybranych branż (branże z karty użytkownika; bez filtra — każda) */
  const personInFilter = (userId: string) => !tradeFilter.length || (board.data?.people.find((p) => p.id === userId)?.tradeCodes ?? []).some((c) => tradeFilter.includes(c));
  /** zadanie dotyczy którejś z wybranych branż (zadania bez branży — tylko bez filtra) */
  const taskInFilter = (t: { scopes: { tradeCode: string }[] }) => !tradeFilter.length || t.scopes.some((x) => tradeFilter.includes(x.tradeCode));
  const days = taskDays(start, to, false);
  const board = useQuery({ queryKey: ['planning-board', start, to], queryFn: () => api<PlanningBoardDto>(`/planning/board?from=${start}&to=${to}`) });
  // gotowość materiałowa zadań realizacji na planie (bez cen; tylko z podglądem zapotrzebowań)
  const execIds = (board.data?.tasks ?? []).filter((t) => t.taskType === 'montaz' || t.taskType === 'uruchomienie').map((t) => t.id).sort();
  const readiness = useQuery({
    queryKey: ['tasks-readiness', execIds.join(',')],
    queryFn: () => api<Record<string, Readiness | null>>(`/tasks-readiness?ids=${execIds.slice(0, 200).join(',')}`),
    enabled: can(me, 'zapotrzebowania.podglad') && execIds.length > 0,
    staleTime: 60_000,
  });
  const queue = useQuery({ queryKey: ['planning-queue'], queryFn: () => api<PlanningQueueDto>('/planning/queue') });
  const trades = useTrades();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // okno planowania; `rest` — kolejne zadania tej samej serii (np. osobne realizacje branż), `step` — numer w serii
  const [planFor, setPlanFor] = useState<{ task: TaskDto; defaults?: PlanDefaults; rest?: TaskDto[]; step?: { index: number; total: number } } | null>(null);
  const planSeries = (tasks: TaskDto[]) => tasks.length && setPlanFor({ task: tasks[0]!, rest: tasks.slice(1), step: { index: 1, total: tasks.length } });
  // ?zadanie=… — otwarcie okna zadania z innego miejsca (np. zgłoszenie serwisowe)
  const [panel, setPanel] = useState<string | null>(() => new URLSearchParams(window.location.search).get('zadanie'));
  const [over, setOver] = useState<string | null>(null);
  // przypisanie od strony ekipy: kliknięty dzień w wierszu ekipy
  const [assign, setAssign] = useState<{ teamId: string; teamName: string; day: string } | null>(null);
  const [filter, setFilter] = useState<QueueFilter>('wszystko');
  // ?szukaj=… — kolejka zawężona (np. do numeru sprawy z podpowiedzi „Zaplanuj montaż”)
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('szukaj') ?? '');
  const { ref: gridRef, width: gridWidth } = useElementSize();
  const canPlan = can(me, 'planowanie.edycja');
  // poniżej md (telefon) zamiast siatki — lista dzień po dniu; kolejka nad listą, zwijana
  const theme = useMantineTheme();
  const mobile = !useMediaQuery(`(min-width: ${theme.breakpoints.md})`, true, { getInitialValueInEffect: false });
  const [queueOpen, setQueueOpen] = useState(() => !!search);
  const dayW = Math.max(days.length > 14 ? MIN_DAY_W_LONG : MIN_DAY_W, Math.floor(((gridWidth || 900) - LABEL_W - 4) / days.length));
  const color = (t: TaskDto) => trades.data?.find((x) => x.code === t.scopes[0]?.tradeCode)?.color ?? TYPE_COLOR[t.taskType] ?? '#495057';
  const name = (id: string) => board.data?.people.find((p) => p.id === id)?.fullName ?? '—';

  // „Zaplanuj realizację” z kolejki: wybór branż sprawy (wspólne / osobne zadania), potem planowanie pierwszego
  const [realization, setRealization] = useState<{ caseId: string; scopeIds: string[] } | null>(null);
  const createFromScope = useMutation({
    mutationFn: (v: { scopeIds: string[]; caseId: string; defaults?: PlanDefaults }) =>
      api<TaskDto>('/tasks', { method: 'POST', body: { taskType: 'montaz', caseId: v.caseId, scopeIds: v.scopeIds } }).then((task) => ({ task, defaults: v.defaults })),
    onSuccess: (r) => {
      void queue.refetch();
      setPlanFor(r);
    },
    onError: notifyError,
  });

  // ?zaplanuj=id[,id…] — okno planowania zadań bez terminu, po kolei (z podpowiedzi i z „Zaplanuj realizację”); bez uprawnienia — szczegóły
  const [toPlan] = useState(() => (new URLSearchParams(window.location.search).get('zaplanuj') ?? '').split(',').filter((x) => /^[0-9a-f-]{36}$/.test(x)));
  useEffect(() => {
    if (!toPlan.length) return;
    if (!canPlan) {
      setPanel(toPlan[0]!);
      return;
    }
    let live = true;
    Promise.all(toPlan.map((id) => api<TaskDto>(`/tasks/${id}`)))
      .then((tasks) => live && planSeries(tasks))
      .catch(notifyError);
    return () => {
      live = false;
    };
  }, [toPlan, canPlan]);

  const startDrag = (e: DragEvent, d: Drag) => {
    e.dataTransfer.setData('application/json', JSON.stringify(d));
    e.dataTransfer.effectAllowed = 'move';
  };
  const drop = (e: DragEvent, teamId: string | null, day: string, userId?: string) => {
    e.preventDefault();
    setOver(null);
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;
    const d = JSON.parse(raw) as Drag;
    // upuszczenie na wiersz osoby — wpis dla tej osoby, bez ekipy
    const defaults: PlanDefaults = userId ? { dateFrom: day, teamId: null, userId } : { dateFrom: day, teamId };
    if (d.type === 'scope') createFromScope.mutate({ scopeIds: d.scopeIds, caseId: d.caseId, defaults });
    else {
      const task = board.data?.tasks.find((t) => t.id === d.id) ?? queue.data?.tasks.find((t) => t.id === d.id) ?? queue.data?.continue.find((t) => t.id === d.id);
      // przeciągnięty pasek = przeniesienie tych dni; zadanie z kolejki = kolejny dzień pracy
      if (task) setPlanFor({ task, defaults: d.type === 'segment' && task.segments.some((x) => x.id === d.segmentId) ? { ...defaults, moveSegmentId: d.segmentId } : defaults });
    }
  };
  const dropProps = (teamId: string | null, day: string, userId?: string) =>
    canPlan && day >= today
      ? {
          onDragOver: (e: DragEvent) => {
            e.preventDefault();
            setOver(userId ? `u:${userId}|${day}` : `${teamId}|${day}`);
          },
          onDragLeave: () => setOver(null),
          onDrop: (e: DragEvent) => drop(e, teamId, day, userId),
        }
      : {};

  const absenceOf = useMemo(() => {
    const m = new Map<string, CalendarEntryDto>();
    for (const e of board.data?.entries ?? []) {
      if (e.kind !== 'nieobecnosc' || e.absenceStatus === 'odrzucona') continue;
      for (const d of taskDays(e.dateFrom, e.dateTo, false)) for (const u of e.userIds) m.set(`${u}|${d}`, e);
    }
    return m;
  }, [board.data]);

  // Flota (docs/19 §6): auto ekipy / osoby niedostępne danego dnia — klucz jak w `over` (ekipa|dzień, u:osoba|dzień)
  const fleetOf = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const b of board.data?.fleetBlocks ?? [])
      for (const d of b.days) {
        const k = b.teamId ? `${b.teamId}|${d}` : `u:${b.userId}|${d}`;
        m.set(k, [...new Set([...(m.get(k) ?? []), b.text])]);
      }
    return m;
  }, [board.data]);
  const fleetOfRow = (key: { teamId?: string; userId?: string }) => [...new Set((board.data?.fleetBlocks ?? []).filter((b) => (key.teamId ? b.teamId === key.teamId : b.userId === key.userId)).map((b) => b.text))];
  /** pomarańczowy pasek u dołu dnia: auto niedostępne (opis po najechaniu) */
  const fleetStrip = (texts: string[] | undefined) =>
    texts?.length ? (
      <Tooltip label={texts.join('\n')} multiline w={300} style={{ whiteSpace: 'pre-line' }}>
        <Box
          aria-label="Auto niedostępne"
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 4, zIndex: 2, cursor: 'help', background: 'repeating-linear-gradient(90deg, var(--mantine-color-orange-6), var(--mantine-color-orange-6) 6px, transparent 6px, transparent 9px)' }}
        />
      </Tooltip>
    ) : null;

  /** wiersz ekipy dla zadania: jej własne + przydzielone osobom, które w dniu startu są w tej ekipie */
  const rows = useMemo(() => {
    const b = board.data;
    if (!b) return { byTeam: new Map<string, Bar[]>(), loose: [] as Bar[], byPerson: new Map<string, Bar[]>() };
    const byTeam = new Map<string, Omit<Bar, 'lane'>[]>();
    const loose: Omit<Bar, 'lane'>[] = [];
    for (const t of b.tasks) {
      for (const seg of segmentsOf(t)) {
        if (seg.dateTo < start || seg.dateFrom > to) continue;
        if (seg.teamId && b.teams.some((tm) => tm.id === seg.teamId)) {
          byTeam.set(seg.teamId, [...(byTeam.get(seg.teamId) ?? []), { task: t, seg, personal: false }]);
          continue;
        }
        // osoby bez ekipy — wiersz ekipy, do której należą w pierwszym widocznym dniu wpisu (czasowe przejścia)
        const day0 = seg.dateFrom < start ? start : seg.dateFrom;
        const users = new Set(t.assignments.filter((a) => a.day >= day0 && a.day <= seg.dateTo).map((a) => a.userId));
        const team = b.teams.find((tm) => tm.days.find((d) => d.day === day0)?.userIds.some((u) => users.has(u)));
        if (team) byTeam.set(team.id, [...(byTeam.get(team.id) ?? []), { task: t, seg, personal: true }]);
        else loose.push({ task: t, seg, personal: true });
      }
    }
    // zadania osób spoza ekip — w wierszu każdej przydzielonej osoby (zadanie dwóch osób jest w obu wierszach)
    const byPerson = new Map<string, Omit<Bar, 'lane'>[]>();
    for (const b0 of loose) {
      const users = new Set(b0.task.assignments.filter((a) => a.day >= b0.seg.dateFrom && a.day <= b0.seg.dateTo && a.state !== 'odwolany').map((a) => a.userId));
      for (const u of users) byPerson.set(u, [...(byPerson.get(u) ?? []), b0]);
    }
    return { byTeam: new Map([...byTeam].map(([k, v]) => [k, lanes(v)])), loose: lanes(loose), byPerson: new Map([...byPerson].map(([k, v]) => [k, lanes(v)])) };
  }, [board.data, start, to]);

  const events = (board.data?.entries ?? []).filter((e) => e.kind !== 'nieobecnosc');
  const grid = { display: 'grid', gridTemplateColumns: `${LABEL_W}px repeat(${days.length}, ${dayW}px)` } as const;
  const col = (d: string) => days.indexOf(d < start ? start : d > to ? to : d) + 2;
  const cellBg = (d: string, key?: string) =>
    over === key ? 'var(--app-today-strong)' : d === today ? 'var(--app-today)' : d < today ? 'var(--app-past)' : dayLabel(d).weekend ? 'var(--app-subtle)' : undefined;

  const bar = ({ task: t, seg, lane, personal }: Bar) => {
    const segAs = t.assignments.filter((a) => a.day >= seg.dateFrom && a.day <= seg.dateTo);
    const many = segmentsOf(t).length > 1;
    // wąski pasek (np. jeden dzień w widoku 2–4 tygodni) — mniejsza czcionka, żeby cokolwiek było widać
    const barW = (col(seg.dateTo) - col(seg.dateFrom) + 1) * dayW;
    const fs = barW < 120 ? 'xs' : 'sm';
    const narrow = barW < 80;
    return (
      <Tooltip
        key={seg.id}
        multiline
        w={300}
        openDelay={300}
        label={
          <Stack gap={2}>
            <Text size="xs" fw={700}>
              {t.number} · {t.title}
            </Text>
            <Text size="xs">
              {termText({ dateFrom: seg.dateFrom, dateTo: seg.dateTo, timeFrom: seg.timeFrom, timeTo: seg.timeTo })}
              {many ? ` · dni pracy ${segmentsOf(t).findIndex((x) => x.id === seg.id) + 1} z ${segmentsOf(t).length}` : ''}
            </Text>
            <Text size="xs">
              {t.clientName ?? '—'} · {t.siteAddress ?? ''}
            </Text>
            <Text size="xs">Pracownicy: {[...new Set(segAs.map((a) => name(a.userId)))].join(', ') || '—'}</Text>
            {readiness.data?.[t.id] && <Text size="xs">Materiały: {READINESS[readiness.data[t.id]!]}</Text>}
            {t.conditional && (
              <Group gap={4} wrap="nowrap">
                <IconLock size={12} style={{ flexShrink: 0 }} />
                <Text size="xs">start zablokowany — warunki startu niespełnione</Text>
              </Group>
            )}
            {t.conflicts.length > 0 && (
              <Group gap={4} wrap="nowrap">
                <IconAlertTriangle size={12} style={{ flexShrink: 0 }} />
                <Text size="xs">konflikty: {t.conflicts.length}</Text>
              </Group>
            )}
            {t.fleetWarnings?.map((w) => (
              <Group key={w} gap={4} wrap="nowrap">
                <IconCarOff size={12} style={{ flexShrink: 0 }} />
                <Text size="xs">{w}</Text>
              </Group>
            ))}
          </Stack>
        }
      >
        <Box
          draggable={t.can.plan}
          onDragStart={(e) => startDrag(e, { type: 'segment', id: t.id, segmentId: seg.id })}
          onClick={() => setPanel(t.id)}
          style={{
            gridColumn: `${col(seg.dateFrom)} / ${col(seg.dateTo) + 1}`,
            gridRow: lane + 1,
            margin: '3px 3px',
            borderRadius: 5,
            background: t.conditional ? `repeating-linear-gradient(135deg, ${color(t)}, ${color(t)} 6px, ${color(t)}bb 6px, ${color(t)}bb 12px)` : personal ? `${color(t)}dd` : color(t),
            border: personal ? '1.5px dashed rgba(255,255,255,.85)' : undefined,
            color: readableTextColor(color(t)),
            padding: narrow ? '4px 4px' : '4px 8px',
            cursor: 'pointer',
            zIndex: 1,
            outline: t.conflicts.length ? '2px solid var(--mantine-color-red-6)' : undefined,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0,
            overflow: 'hidden',
            minWidth: 0,
            opacity: !taskInFilter(t) ? 0.3 : t.status === 'zakonczone' ? 0.55 : 1,
          }}
        >
          {/* dwa rzędy jak wpis w Kalendarzu: 1 — tytuł pracy, 2 — numer zadania i godziny (klient, adres, osoby — w dymku) */}
          <Group gap={narrow ? 2 : 4} wrap="nowrap" style={{ minWidth: 0 }}>
            {t.conditional && <IconLock size={narrow ? 11 : 13} style={{ flexShrink: 0 }} />}
            <ConflictMark conflicts={t.conflicts} />
            <FleetMark warnings={t.fleetWarnings} size={narrow ? 11 : 13} />
            <ReadinessMark readiness={readiness.data?.[t.id]} />
            <Text size={narrow ? 'xs' : fs} fw={600} truncate lh="20px" style={{ minWidth: 0 }}>
              {many ? '⋯ ' : ''}
              {t.title || typeLabel(t)}
            </Text>
          </Group>
          <Text size="xs" truncate lh="16px" style={{ opacity: 0.85 }}>
            {narrow ? (seg.timeFrom ?? t.number) : `${t.number} · ${seg.timeFrom ? `${seg.timeFrom}–${seg.timeTo ?? ''}` : 'cały dzień'}`}
          </Text>
        </Box>
      </Tooltip>
    );
  };

  const personRow = (userId: string, key: string) => (
    <Box key={key} style={{ ...grid, borderBottom: '1px solid var(--app-line)' }}>
      <Text size="sm" px={8} pl={28} truncate style={{ ...STICKY, alignSelf: 'center' }}>
        {name(userId)}
      </Text>
      {days.map((d) => {
        const abs = absenceOf.get(`${userId}|${d}`);
        const mine = (board.data?.tasks ?? []).filter((t) => t.assignments.some((a) => a.userId === userId && a.day === d));
        const conflict = mine.some((t) => t.conflicts.some((c) => c.userId === userId && c.day === d));
        return (
          <Tooltip key={d} label={abs ? `${abs.title}${abs.absenceStatus === 'wniosek' ? ' (wniosek)' : ''}` : mine.map((t) => `${t.timeFrom ?? ''} ${surname(t.clientName) || t.number} · ${typeLabel(t)}`).join('\n')} disabled={!abs && !mine.length} multiline style={{ whiteSpace: 'pre-line' }}>
            <Box
              {...dropProps(null, d, userId)}
              style={{
                borderLeft: '1px solid var(--app-line)',
                minHeight: 36,
                padding: '4px 3px',
                display: 'flex',
                alignItems: 'center',
                background: abs
                  ? `repeating-linear-gradient(45deg, ${abs.absenceStatus === 'wniosek' ? 'var(--app-absence-request)' : 'var(--app-absence)'}, ${abs.absenceStatus === 'wniosek' ? 'var(--app-absence-request)' : 'var(--app-absence)'} 4px, transparent 4px, transparent 8px)`
                  : cellBg(d, `u:${userId}|${d}`),
              }}
            >
              <Group gap={3} wrap="nowrap" style={{ flex: 1 }}>
                {mine.slice(0, 3).map((t) => (
                  <Box key={t.id} h={14} style={{ flex: 1, maxWidth: 22, borderRadius: 3, background: color(t), outline: conflict ? '2px solid var(--mantine-color-red-6)' : undefined }} />
                ))}
                {mine.length > 3 && <Text size="10px">+{mine.length - 3}</Text>}
              </Group>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );

  const loosePeople = [...new Set([...(board.data?.unassignedPeople ?? []), ...rows.byPerson.keys()])]
    .filter((u) => personInFilter(u) || (rows.byPerson.get(u) ?? []).some((b) => taskInFilter(b.task)))
    .sort((a, b) => name(a).localeCompare(name(b), 'pl'));
  /** wiersz osoby spoza ekip: paski jej zadań (jak w wierszu ekipy), nieobecności w tle, upuszczenie = wpis dla tej osoby */
  const loosePersonRow = (userId: string) => {
    const bars = rows.byPerson.get(userId) ?? [];
    const laneCount = Math.max(1, ...bars.map((x) => x.lane + 1));
    const absBg = (d: string) => {
      const abs = absenceOf.get(`${userId}|${d}`);
      return abs ? `repeating-linear-gradient(45deg, ${abs.absenceStatus === 'wniosek' ? 'var(--app-absence-request)' : 'var(--app-absence)'}, ${abs.absenceStatus === 'wniosek' ? 'var(--app-absence-request)' : 'var(--app-absence)'} 4px, transparent 4px, transparent 8px)` : null;
    };
    const p = board.data?.people.find((x) => x.id === userId);
    return (
      <Box key={`osoba-${userId}`} style={{ ...grid, gridTemplateRows: `repeat(${laneCount}, ${LANE_H}px)`, borderBottom: '1px solid var(--app-line)', position: 'relative' }}>
        <Group gap={6} px={8} pl={16} wrap="nowrap" style={{ ...STICKY, gridRow: `1 / span ${laneCount}`, gridColumn: 1 }}>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} truncate lh={1.3}>
              {name(userId)}
            </Text>
            {!!p?.tradeCodes.length && (
              <Group gap={2} wrap="nowrap">
                {p.tradeCodes.slice(0, 4).map((c) => (
                  <TradeBadge key={c} code={c} />
                ))}
              </Group>
            )}
          </Box>
          <FleetMark warnings={fleetOfRow({ userId })} />
        </Group>
        {days.map((d, i) => {
          const abs = absenceOf.get(`${userId}|${d}`);
          return (
            <Box
              key={d}
              {...dropProps(null, d, userId)}
              title={abs ? `${abs.title}${abs.absenceStatus === 'wniosek' ? ' (wniosek)' : ''}` : canPlan && d >= today ? 'Przeciągnij tu zadanie, aby zaplanować je temu pracownikowi' : undefined}
              style={{ gridColumn: i + 2, gridRow: `1 / span ${laneCount}`, borderLeft: '1px solid var(--app-line)', background: absBg(d) ?? cellBg(d, `u:${userId}|${d}`), position: 'relative' }}
            >
              {fleetStrip(fleetOf.get(`u:${userId}|${d}`))}
            </Box>
          );
        })}
        {bars.map(bar)}
      </Box>
    );
  };

  // ── kolejka ──
  const q = search.trim().toLowerCase();
  const match = (...xs: (string | null | undefined)[]) => !q || xs.some((x) => x?.toLowerCase().includes(q));
  const service = (queue.data?.tasks ?? []).filter((t) => t.ticketId && taskInFilter(t) && match(t.number, t.title, t.ticketNumber, t.clientName, t.siteAddress));
  const other = (queue.data?.tasks ?? []).filter((t) => !t.ticketId && taskInFilter(t) && match(t.number, t.title, t.caseNumber, t.clientName, t.siteAddress));
  const scopes = (queue.data?.scopes ?? []).filter((s) => (!tradeFilter.length || s.tradeCodes.some((c) => tradeFilter.includes(c))) && match(s.caseNumber, s.clientName, s.city, s.tradeCode));
  const cont = (queue.data?.continue ?? []).filter((t) => taskInFilter(t) && match(t.number, t.title, t.caseNumber, t.ticketNumber, t.clientName, t.siteAddress));
  const openTask = (t: TaskDto) => (canPlan ? setPlanFor({ task: t }) : setPanel(t.id));

  const taskCard = (t: TaskDto) => (
    <QueueCard
      key={t.id}
      draggable={canPlan && !t.onHold}
      onDragStart={(e) => startDrag(e, { type: 'task', id: t.id })}
      onClick={() => (t.onHold ? setPanel(t.id) : openTask(t))}
      color={color(t)}
      top={
        <>
          {t.onHold && (
            <Tooltip label={`${t.onHold.what === 'sprawa' ? 'Sprawa wstrzymana' : 'Zakres wstrzymany'}${t.onHold.reason ? `: ${t.onHold.reason}` : ''} — zaplanujesz po wznowieniu`} multiline w={260}>
              <Badge size="xs" variant="light" color="yellow">
                wstrzymane{t.onHold.until ? ` do ${datePl(t.onHold.until)}` : ''}
              </Badge>
            </Tooltip>
          )}
          <Badge size="xs" variant="light" color="gray">
            {typeLabel(t)}
          </Badge>
          {t.scopes.slice(0, 2).map((s) => (
            <TradeBadge key={s.id} code={s.tradeCode} />
          ))}
        </>
      }
      title={t.clientName ?? t.title}
      sub={`${t.siteAddress ?? ''} · ${t.ticketNumber ?? t.caseNumber ?? t.number}`}
      canPlan={canPlan && !t.onHold}
    />
  );
  const sections: { key: QueueFilter; label: string; count: number; body: ReactNode }[] = [
    {
      key: 'kontynuacja',
      label: 'W toku',
      count: cont.length,
      body: cont.map((t) => (
        <QueueCard
          key={t.id}
          draggable={canPlan}
          onDragStart={(e) => startDrag(e, { type: 'task', id: t.id })}
          onClick={() => openTask(t)}
          color={color(t)}
          top={
            <>
              <Badge size="xs" variant="light" color="orange">
                {t.dateTo && t.dateTo >= today ? 'brak pracowników w planie' : t.status === 'w_toku' ? 'w toku — bez kolejnych dni' : 'bez kolejnych dni'}
              </Badge>
              <Badge size="xs" variant="light" color="gray">
                {typeLabel(t)}
              </Badge>
            </>
          }
          title={t.clientName ?? t.title}
          sub={`ostatni dzień ${t.dateTo ? dayLabel(t.dateTo).dm : '—'} · ${t.ticketNumber ?? t.caseNumber ?? t.number}`}
          canPlan={canPlan}
          action="Dalsze dni ›"
        />
      )),
    },
    { key: 'serwis', label: 'Serwis i przeglądy', count: service.length, body: service.map(taskCard) },
    {
      key: 'montaze',
      label: 'Realizacje',
      count: scopes.length,
      body: scopes.map((s) => (
        <QueueCard
          key={s.contractId}
          draggable={canPlan && !s.onHold}
          onDragStart={(e) => startDrag(e, { type: 'scope', scopeIds: s.scopeIds, caseId: s.caseId })}
          onClick={() => canPlan && !s.onHold && setRealization({ caseId: s.caseId, scopeIds: s.scopeIds })}
          color={trades.data?.find((x) => x.code === s.tradeCode)?.color ?? '#495057'}
          top={
            <>
              {/* QA qa10310325 P4: wstrzymana sprawa / zakresy — jak przy kartach zadań */}
              {s.onHold && (
                <Tooltip label={`${s.onHold.what === 'sprawa' ? 'Sprawa wstrzymana' : 'Zakres wstrzymany'}${s.onHold.reason ? `: ${s.onHold.reason}` : ''} — zaplanujesz po wznowieniu`} multiline w={260}>
                  <Badge size="xs" variant="light" color="yellow">
                    wstrzymane{s.onHold.until ? ` do ${datePl(s.onHold.until)}` : ''}
                  </Badge>
                </Tooltip>
              )}
              <Badge size="xs" variant="light" color="gray">
                Realizacja
              </Badge>
              {s.tradeCodes.map((c) => (
                <TradeBadge key={c} code={c} />
              ))}
              <CompanyBadgeById id={s.companyId} />
            </>
          }
          lock={!s.conditionsMet ? `Warunki startu niespełnione: ${s.conditionsText}. Można zaplanować warunkowo — ekipa nie rozpocznie, dopóki nie zostaną spełnione.` : undefined}
          title={s.clientName}
          sub={`${s.city} · ${s.contractNumber ?? s.caseNumber}`}
          canPlan={canPlan && !s.onHold}
        />
      )),
    },
    { key: 'inne', label: 'Inne', count: other.length, body: other.map(taskCard) },
  ];
  const total = sections.reduce((a, s) => a + s.count, 0);

  // ── kolejka „Do zaplanowania” — na telefonie zwijana, nad listą dni ──
  const queueBody = (
    <>
      <TextInput size="xs" leftSection={<IconSearch size={14} />} placeholder="Klient, miejscowość, numer" value={search} onChange={(e) => setSearch(e.currentTarget.value)} mb={6} />
      <Chip.Group value={filter} onChange={(v) => setFilter(v as QueueFilter)}>
        <Group gap={4} mb={8}>
          {/* te same nazwy co sekcje kolejki */}
          {[{ value: 'wszystko', label: 'Wszystko' }, ...sections.map((s) => ({ value: s.key, label: s.label }))].map((c) => (
            <Chip key={c.value} value={c.value} size="xs" variant="light">
              {c.label}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
      <QueryState isLoading={queue.isLoading} error={queue.error}>
        {/* cienki pasek obok kart (offset), a nie na nich */}
        <ScrollArea.Autosize mah={mobile ? '60vh' : 'calc(100vh - 290px)'} scrollbars="y" offsetScrollbars="y" scrollbarSize={6} className="scroll-fit">
          <Stack gap={10}>
            {sections
              .filter((s) => (filter === 'wszystko' ? s.count > 0 : s.key === filter))
              .map((s) => (
                <Stack key={s.key} gap={6}>
                  {(filter === 'wszystko' || s.key === 'kontynuacja') && (
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                      {s.label} · {s.count}
                    </Text>
                  )}
                  {s.body}
                  {s.count === 0 && <EmptyState compact title={q ? 'Brak wyników' : 'Nic do zaplanowania'} />}
                </Stack>
              ))}
            {total === 0 && filter === 'wszystko' && <EmptyState compact title={q ? 'Brak wyników' : 'Kolejka jest pusta'} hint={q ? 'Zmień wyszukiwane słowa.' : undefined} />}
          </Stack>
        </ScrollArea.Autosize>
      </QueryState>
    </>
  );
  const queueCard = mobile ? (
    <Card withBorder padding="xs" mb="sm">
      <UnstyledButton onClick={() => setQueueOpen((o) => !o)} w="100%">
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            {queueOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            <Text fw={600} size="sm">
              Do zaplanowania
            </Text>
          </Group>
          <Badge variant="light">{total}</Badge>
        </Group>
      </UnstyledButton>
      <Collapse expanded={queueOpen}>
        <Box pt={8}>{queueBody}</Box>
      </Collapse>
    </Card>
  ) : (
    <Card withBorder padding="xs" w={280} style={{ flexShrink: 0 }}>
      <Group justify="space-between" mb={6}>
        <Text fw={600} size="sm">
          Do zaplanowania
        </Text>
        <Badge variant="light">{total}</Badge>
      </Group>
      {queueBody}
    </Card>
  );

  // ── telefon: lista dzień po dniu (ekipa → zadania danego dnia) zamiast siatki ──
  const dayGroups = [
    ...(board.data?.teams ?? []).filter(teamInFilter).map((tm) => ({ key: tm.id, name: tm.name, color: tm.color, bars: (rows.byTeam.get(tm.id) ?? []).filter((b) => taskInFilter(b.task)) })),
    { key: 'spoza', name: 'Pracownicy spoza ekip', color: 'var(--mantine-color-gray-5)', bars: rows.loose },
  ];
  const absences = (board.data?.entries ?? []).filter((e) => e.kind === 'nieobecnosc' && e.absenceStatus !== 'odrzucona');
  const dayItem = ({ task: t, seg, personal }: Bar) => {
    const segAs = t.assignments.filter((a) => a.day >= seg.dateFrom && a.day <= seg.dateTo);
    const who = personal ? [...new Set(segAs.map((a) => surname(name(a.userId))))].join(', ') : '';
    const place = t.clientName || t.siteAddress || t.ticketNumber || t.number;
    return (
      <UnstyledButton
        key={seg.id}
        onClick={() => setPanel(t.id)}
        w="100%"
        style={{
          display: 'block',
          borderLeft: `4px ${personal ? 'dashed' : 'solid'} ${color(t)}`,
          borderRadius: 4,
          padding: '6px 8px',
          background: 'var(--mantine-color-default-hover)',
          outline: t.conflicts.length ? '1.5px solid var(--mantine-color-red-6)' : undefined,
          opacity: t.status === 'zakonczone' ? 0.55 : 1,
        }}
      >
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          <Text size="sm" fw={700} style={{ flexShrink: 0 }}>
            {seg.timeFrom ? `${seg.timeFrom}–${seg.timeTo ?? ''}` : 'cały dzień'}
          </Text>
          {t.conditional && <IconLock size={14} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />}
          <ConflictMark conflicts={t.conflicts} />
          <Text size="sm" fw={600} truncate style={{ minWidth: 0 }}>
            {place}
          </Text>
        </Group>
        <Text size="xs" c="dimmed" truncate>
          {typeLabel(t)} · {t.number}
          {t.title ? ` · ${t.title}` : ''}
          {t.siteAddress && t.clientName ? ` · ${t.siteAddress}` : ''}
        </Text>
        {who && (
          <Text size="xs" c="dimmed" truncate>
            Pracownicy: {who}
          </Text>
        )}
      </UnstyledButton>
    );
  };
  const dayList = (
    <QueryState isLoading={board.isLoading} error={board.error}>
      <Stack gap="sm">
        {days.map((d) => {
          const isToday = d === today;
          const weekend = dayLabel(d).weekend;
          const groups = dayGroups
            .map((gr) => ({ ...gr, bars: gr.bars.filter((b) => segOnDay(b.seg, d)).sort((a, b) => (a.seg.timeFrom ?? '').localeCompare(b.seg.timeFrom ?? '')) }))
            .filter((gr) => gr.bars.length > 0);
          const dayEvents = events.filter((e) => e.dateFrom <= d && e.dateTo >= d);
          const dayAbs = absences.filter((e) => e.dateFrom <= d && e.dateTo >= d);
          const dayFleet = (board.data?.fleetBlocks ?? [])
            .filter((b) => b.days.includes(d))
            .map((b) => ({ key: `${b.unavailabilityId}|${b.teamId ?? b.userId}`, who: b.teamId ? (board.data?.teams.find((x) => x.id === b.teamId)?.name ?? 'Ekipa') : name(b.userId ?? ''), text: b.text }));
          return (
            <Card key={d} withBorder padding={0}>
              <Group
                gap={8}
                px="sm"
                py={6}
                style={{
                  background: isToday ? 'var(--mantine-color-blue-light)' : weekend ? 'var(--mantine-color-default-hover)' : undefined,
                  borderBottom: '1px solid var(--mantine-color-default-border)',
                }}
              >
                <Text fw={700} c={isToday ? 'blue.7' : weekend ? 'red.7' : undefined}>
                  {wdShort(d)} {dayLabel(d).dm}
                </Text>
                {isToday && (
                  <Badge size="sm" variant="filled">
                    dziś
                  </Badge>
                )}
              </Group>
              <Stack gap={8} p="sm">
                {dayEvents.map((e) => (
                  <Text key={e.id} size="xs" c="dimmed" truncate>
                    {e.timeFrom ? `${e.timeFrom}–${e.timeTo ?? ''} ` : ''}
                    {e.title}
                  </Text>
                ))}
                {dayFleet.map((f) => (
                  <Group key={f.key} gap={4} wrap="nowrap">
                    <IconCarOff size={14} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />
                    <Text size="xs" c="orange.8" truncate>
                      {f.who}: {f.text}
                    </Text>
                  </Group>
                ))}
                {dayAbs.map((e) => (
                  <Text key={e.id} size="xs" c="dimmed" truncate>
                    Nieobecność: {e.userIds.map(name).join(', ')} — {e.title}
                    {e.absenceStatus === 'wniosek' ? ' (wniosek)' : ''}
                  </Text>
                ))}
                {groups.map((gr) => (
                  <Box key={gr.key}>
                    <Group gap={6} mb={4} wrap="nowrap">
                      <Box w={4} h={16} style={{ background: gr.color, borderRadius: 2, flexShrink: 0 }} />
                      <Text size="sm" fw={600} truncate>
                        {gr.name}
                      </Text>
                    </Group>
                    <Stack gap={4}>{gr.bars.map(dayItem)}</Stack>
                  </Box>
                ))}
                {groups.length === 0 && (
                  <Text size="sm" c="dimmed">
                    Nic nie zaplanowano.
                  </Text>
                )}
              </Stack>
            </Card>
          );
        })}
      </Stack>
    </QueryState>
  );

  return (
    <>
      <PageHeader
        title="Planowanie"
        description={
          mobile
            ? 'Plan ekip dzień po dniu — dotknij zadania, aby zobaczyć szczegóły.'
            : 'Zadanie → ekipa: kliknij pozycję w kolejce albo przeciągnij ją na dzień. Ekipa → zadanie: kliknij dzień w wierszu ekipy.'
        }
      />
      {/* pasek narzędzi: nawigacja po dniach, filtr branż, zakres; legenda po prawej */}
      <Group justify="space-between" align="center" mb="sm" wrap="wrap" gap="xs">
        <Group gap="xs" wrap="wrap">
          <ActionIcon variant="default" onClick={() => setStart(shiftDate(start, -Number(span)))} aria-label="Wcześniej">
            <IconChevronLeft size={16} />
          </ActionIcon>
          <Button variant="default" size="xs" onClick={() => setStart(today)}>
            Dziś
          </Button>
          <ActionIcon variant="default" onClick={() => setStart(shiftDate(start, Number(span)))} aria-label="Dalej">
            <IconChevronRight size={16} />
          </ActionIcon>
          <Text size="sm" fw={500}>
            {dayRange(start, to)}
          </Text>
          {board.isFetching ? <Loader size="xs" /> : <Box w={16} />}
          <TradeFilter trades={trades.data ?? []} value={tradeFilter} onChange={setTradeFilter} fullWidth={mobile} />
          <SegmentedControl
            size="xs"
            value={span}
            onChange={setSpan}
            data={[
              { value: '7', label: 'Tydzień' },
              { value: '14', label: '2 tygodnie' },
              { value: '28', label: '4 tygodnie' },
            ]}
          />
        </Group>
        <Legend fleet={board.data?.fleetBlocks !== undefined} />
      </Group>
      {mobile ? (
        <>
          {queueCard}
          {dayList}
        </>
      ) : (
      <Group align="flex-start" gap="md" wrap="nowrap">
        {queueCard}

        {/* siatka */}
        <Card withBorder padding={0} style={{ flex: 1, minWidth: 0 }} ref={gridRef}>
          <QueryState isLoading={board.isLoading} error={board.error}>
            <ScrollArea type="auto">
              <Box style={{ minWidth: LABEL_W + days.length * dayW }}>
                <Box style={{ ...grid, position: 'sticky', top: 0, zIndex: 2, background: 'var(--mantine-color-body)', borderBottom: '1px solid var(--app-line-strong)' }}>
                  <Text size="sm" c="dimmed" fw={500} px={10} style={{ ...STICKY, alignSelf: 'center' }}>
                    Ekipa / pracownik
                  </Text>
                  {days.map((d) => {
                    const l = dayLabel(d);
                    const isToday = d === today;
                    return (
                      <Box
                        key={d}
                        ta="center"
                        py={6}
                        style={{
                          borderLeft: '1px solid var(--app-line)',
                          background: isToday ? 'var(--app-today-strong)' : l.weekend ? 'var(--app-subtle)' : undefined,
                          boxShadow: isToday ? 'inset 0 -3px 0 var(--mantine-color-blue-6)' : undefined,
                        }}
                      >
                        <Text size="xs" tt="uppercase" c={isToday ? 'blue.7' : l.weekend ? 'red' : 'dimmed'} fw={isToday ? 700 : 500} lh={1.3}>
                          {isToday ? 'dziś' : l.wd}
                        </Text>
                        <Text size={dayW >= 52 ? 'md' : 'sm'} fw={700} c={isToday ? 'var(--app-text-blue)' : undefined} lh={1.3}>
                          {l.dm}
                        </Text>
                      </Box>
                    );
                  })}
                </Box>
                {events.length > 0 && (
                  <Box style={{ ...grid, gridAutoRows: 'minmax(28px, auto)', borderBottom: '1px solid var(--app-line-strong)', background: 'var(--app-subtle)' }}>
                    <Text size="sm" c="dimmed" px={10} style={{ ...STICKY, background: 'var(--app-subtle)', gridRow: `1 / span ${events.length}`, alignSelf: 'center' }}>
                      Wydarzenia i wpisy
                    </Text>
                    {events.map((e, i) => (
                      <Tooltip key={e.id} label={`${e.title}${e.timeFrom ? `, ${e.timeFrom}–${e.timeTo}` : ''}`}>
                        <Box style={{ gridColumn: `${col(e.dateFrom)} / ${col(e.dateTo) + 1}`, gridRow: i + 1, margin: 3, borderRadius: 5, padding: '2px 7px', display: 'flex', alignItems: 'center', minWidth: 0, background: e.kind === 'wydarzenie' ? 'var(--app-chip-event)' : 'var(--app-chip-entry)', color: 'var(--app-chip-text)' }}>
                          <Text size="sm" truncate>
                            {e.title}
                          </Text>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                )}
                {board.data?.teams.filter(teamInFilter).map((team) => {
                  const teamBars = rows.byTeam.get(team.id) ?? [];
                  const laneCount = Math.max(1, ...teamBars.map((x) => x.lane + 1));
                  const open = expanded.has(team.id);
                  const memberIds = [...new Set(team.days.flatMap((d) => d.userIds))];
                  return (
                    <Fragment key={team.id}>
                      <Box style={{ ...grid, gridTemplateRows: `repeat(${laneCount}, ${LANE_H}px)`, borderBottom: '1px solid var(--app-line-strong)', position: 'relative' }}>
                        <Tooltip label={memberIds.map(name).join(', ') || 'brak składu'} openDelay={300}>
                          <Group
                            gap={6}
                            px={8}
                            wrap="nowrap"
                            style={{ ...STICKY, gridRow: `1 / span ${laneCount}`, gridColumn: 1, borderLeft: `5px solid ${team.color}`, cursor: 'pointer' }}
                            onClick={() => setExpanded((s) => (s.has(team.id) ? new Set([...s].filter((x) => x !== team.id)) : new Set([...s, team.id])))}
                          >
                            {open ? <IconChevronDown size={16} style={{ flexShrink: 0 }} /> : <IconChevronRight size={16} style={{ flexShrink: 0 }} />}
                            {/* nazwa ekipy i liczba pracowników w dwóch liniach */}
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text size="md" fw={600} truncate lh={1.3}>
                                {team.name}
                              </Text>
                              <Text size="xs" c="dimmed" lh={1.3}>
                                {memberIds.length} {plural(memberIds.length, 'pracownik', 'pracowników', 'pracowników')}
                              </Text>
                            </Box>
                            <FleetMark warnings={fleetOfRow({ teamId: team.id })} size={16} />
                          </Group>
                        </Tooltip>
                        {days.map((d, i) => (
                          <Box
                            key={d}
                            {...dropProps(team.id, d)}
                            onClick={canPlan && d >= today ? () => setAssign({ teamId: team.id, teamName: team.name, day: d }) : undefined}
                            title={canPlan && d >= today ? 'Kliknij, aby przypisać zadanie tej ekipie' : undefined}
                            style={{ gridColumn: i + 2, gridRow: `1 / span ${laneCount}`, borderLeft: '1px solid var(--app-line)', background: cellBg(d, `${team.id}|${d}`), cursor: canPlan && d >= today ? 'cell' : undefined, position: 'relative' }}
                          >
                            {team.days[i]?.userIds.length === 0 && (
                              <Text size="10px" c="dimmed" ta="center" pt={2}>
                                brak składu
                              </Text>
                            )}
                            {fleetStrip(fleetOf.get(`${team.id}|${d}`))}
                          </Box>
                        ))}
                        {teamBars.map(bar)}
                      </Box>
                      {open && memberIds.map((u) => personRow(u, `${team.id}-${u}`))}
                    </Fragment>
                  );
                })}
                {loosePeople.length > 0 && (
                  <>
                    <Box style={{ ...grid, borderBottom: '1px solid var(--app-line)', background: 'var(--app-subtle)' }}>
                      <Text size="xs" fw={600} c="dimmed" px={10} py={4} style={{ ...STICKY, background: 'var(--app-subtle)' }}>
                        Pracownicy spoza ekip
                      </Text>
                    </Box>
                    {loosePeople.map(loosePersonRow)}
                  </>
                )}
              </Box>
            </ScrollArea>
          </QueryState>
        </Card>
      </Group>
      )}
      {planFor && (
        <PlanDialog
          key={planFor.task.id}
          me={me}
          task={planFor.task}
          defaults={planFor.defaults}
          step={planFor.step}
          onDetails={() => (setPanel(planFor.task.id), setPlanFor(null))}
          onClose={() => {
            // seria: zapis albo „Pomiń” — następne zadanie; ostatnie — koniec
            const [next, ...rest] = planFor.rest ?? [];
            setPlanFor(next ? { task: next, rest, step: planFor.step && { ...planFor.step, index: planFor.step.index + 1 } } : null);
            void queue.refetch();
          }}
        />
      )}
      {panel && <TaskPanel me={me} taskId={panel} onClose={() => setPanel(null)} />}
      {assign && (
        <AssignDialog
          teamName={assign.teamName}
          day={assign.day}
          fleetWarnings={fleetOf.get(`${assign.teamId}|${assign.day}`)}
          queue={queue.data}
          active={(board.data?.tasks ?? []).filter((t) => ['zaplanowane', 'w_toku'].includes(t.status) && !segmentsOf(t).some((x) => x.teamId === assign.teamId && x.dateFrom <= assign.day && x.dateTo >= assign.day))}
          onClose={() => setAssign(null)}
          onTask={(t) => {
            setPlanFor({ task: t, defaults: { dateFrom: assign.day, teamId: assign.teamId } });
            setAssign(null);
          }}
          onScope={(sc) => {
            createFromScope.mutate({ scopeIds: sc.scopeIds, caseId: sc.caseId, defaults: { dateFrom: assign.day, teamId: assign.teamId } });
            setAssign(null);
          }}
        />
      )}
      {realization && (
        <RealizationDialog
          caseId={realization.caseId}
          preselect={realization.scopeIds}
          onClose={() => setRealization(null)}
          onDone={(tasks) => {
            setRealization(null);
            void queue.refetch();
            planSeries(tasks);
          }}
        />
      )}
    </>
  );
}

function QueueCard(p: {
  draggable: boolean;
  onDragStart: (e: DragEvent) => void;
  onClick: () => void;
  color: string;
  top: ReactNode;
  title: string;
  sub: string;
  lock?: string;
  canPlan: boolean;
  action?: string;
}) {
  return (
    <Card
      withBorder
      padding={8}
      draggable={p.draggable}
      onDragStart={p.onDragStart}
      onClick={p.onClick}
      className="card-link"
      style={{ borderLeft: `4px solid ${p.color}`, minWidth: 0, overflow: 'hidden' }}
    >
      <Group gap={4} wrap="nowrap" justify="space-between">
        <Group gap={4} wrap="wrap" style={{ minWidth: 0 }}>
          {p.top}
        </Group>
        {p.lock && (
          <Tooltip label={p.lock} multiline w={240}>
            <IconLock size={14} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />
          </Tooltip>
        )}
      </Group>
      <Text size="sm" fw={600} truncate mt={4}>
        {p.title}
      </Text>
      <Group justify="space-between" wrap="nowrap" gap={4}>
        <Text size="xs" c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>
          {p.sub}
        </Text>
        {p.canPlan && (
          <Text size="xs" c="var(--app-link)" fw={600} style={{ flexShrink: 0 }}>
            {p.action ?? 'Zaplanuj ›'}
          </Text>
        )}
      </Group>
    </Card>
  );
}

function Legend({ fleet }: { fleet: boolean }) {
  const item = (box: ReactNode, label: string) => (
    <Group gap={4} wrap="nowrap">
      {box}
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
  return (
    <Group gap="sm" visibleFrom="lg">
      {item(<IconLock size={12} />, 'start zablokowany')}
      {item(<IconAlertTriangle size={12} color="var(--mantine-color-red-6)" />, 'konflikt')}
      {item(<Box w={14} h={10} style={{ border: '1.5px dashed var(--mantine-color-gray-6)', borderRadius: 2 }} />, 'przydział pracowników (bez ekipy)')}
      {item(<Box w={12} h={12} style={{ background: 'repeating-linear-gradient(45deg, var(--app-absence), var(--app-absence) 3px, transparent 3px, transparent 6px)' }} />, 'nieobecność')}
      {fleet && item(<IconCarOff size={12} color="var(--mantine-color-orange-6)" />, 'auto niedostępne')}
    </Group>
  );
}
